import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { Calculator as CalcIcon, X, Delete, Trash2, GripHorizontal } from "lucide-react";

/**
 * In-game scientific calculator. Themed to match StudyMateRanked.
 * Supports:
 *   • Arithmetic: + − × ÷
 *   • Exponents: x^y, x², x³
 *   • Roots: √
 *   • Trigonometry: sin, cos, tan (degree/radian toggle)
 *   • Logarithms: log (base 10), ln (natural)
 *   • Constants: π, e
 *   • Parentheses, factorial, percent, sign toggle, fractions
 */
export default function Calculator({ open, onClose }) {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("0");
  const [degrees, setDegrees] = useState(true);
  const [memory, setMemory] = useState(0);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const constraintsRef = useRef(null);
  const dragControls = useDragControls();

  // Live-evaluate on every change
  useEffect(() => {
    if (!expr.trim()) { setResult("0"); return; }
    try {
      const val = evaluate(expr, { degrees });
      if (val === undefined || Number.isNaN(val) || !Number.isFinite(val)) {
        setResult("—");
      } else {
        setResult(formatNumber(val));
      }
    } catch (_e) {
      setResult("…");
    }
  }, [expr, degrees]);

  const press = (text) => setExpr((s) => s + text);
  const backspace = () => setExpr((s) => s.slice(0, -1));
  const clearAll = () => { setExpr(""); setResult("0"); };

  const equals = () => {
    try {
      const val = evaluate(expr, { degrees });
      if (val === undefined || Number.isNaN(val) || !Number.isFinite(val)) {
        setResult("Error");
        return;
      }
      const formatted = formatNumber(val);
      setHistory((h) => [{ expr, result: formatted }, ...h].slice(0, 8));
      setResult(formatted);
      setExpr(String(val));
    } catch (_e) {
      setResult("Error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        // No backdrop — a pointer-events-none layer lets the question stay
        // visible AND interactive behind the floating calculator.
        <div ref={constraintsRef} className="fixed inset-0 z-[110] pointer-events-none" data-testid="calculator-layer">
          <motion.div
            drag
            dragControls={dragControls}
            dragListener={false}
            dragMomentum={false}
            dragConstraints={constraintsRef}
            dragElastic={0.04}
            initial={{ scale: 0.92, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 12 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="sm-card pointer-events-auto absolute right-3 top-20 sm:right-6 sm:top-24 w-[min(92vw,22rem)] max-h-[85vh] overflow-y-auto p-4 sm:p-5 shadow-2xl"
            style={{ borderColor: "rgba(168,85,247,0.55)", boxShadow: "0 0 40px rgba(168,85,247,0.30)" }}
            data-testid="calculator-modal"
          >
            {/* Header doubles as the drag handle */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              className="flex items-center gap-2 mb-3 cursor-move touch-none select-none"
            >
              <div className="w-9 h-9 grid place-items-center rounded-lg border border-purple-500/40 bg-purple-500/10">
                <CalcIcon size={16} className="text-purple" />
              </div>
              <div className="font-heading font-black text-lg tracking-wide">SCIENTIFIC CALC</div>
              <GripHorizontal size={16} className="text-faint" />
              <div className="flex-1" />
              <button
                onClick={() => setDegrees((d) => !d)}
                className={`px-2.5 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest border ${
                  degrees ? "border-blue-500/50 text-blue bg-blue-500/10" : "border-purple-500/50 text-purple bg-purple-500/10"
                }`}
                data-testid="calc-deg-rad"
                title="Toggle degree/radian"
              >
                {degrees ? "DEG" : "RAD"}
              </button>
              <button onClick={onClose} aria-label="Close" className="ml-1 p-1 text-faint hover:text-white" data-testid="calc-close">
                <X size={16} />
              </button>
            </div>

            {/* Display */}
            <div className="bg-[#05060F] border border-white/8 rounded-xl px-4 py-3 mb-3">
              <input
                ref={inputRef}
                value={expr}
                onChange={(e) => setExpr(e.target.value)}
                placeholder="Tap a key, or type…"
                className="w-full bg-transparent outline-none font-mono text-right text-soft text-sm sm:text-base"
                data-testid="calc-expr"
              />
              <div className="text-right font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1 text-white truncate" data-testid="calc-result">
                {result}
              </div>
            </div>

            {/* Scientific row */}
            <div className="grid grid-cols-5 gap-2 mb-2">
              <Key label="sin" onClick={() => press("sin(")} accent="purple" testid="calc-sin" />
              <Key label="cos" onClick={() => press("cos(")} accent="purple" testid="calc-cos" />
              <Key label="tan" onClick={() => press("tan(")} accent="purple" testid="calc-tan" />
              <Key label="log" onClick={() => press("log(")} accent="purple" testid="calc-log" />
              <Key label="ln"  onClick={() => press("ln(")}  accent="purple" testid="calc-ln" />
              <Key label="√"   onClick={() => press("√(")}   accent="purple" testid="calc-sqrt" />
              <Key label="x²"  onClick={() => press("^2")}   accent="purple" testid="calc-sq" />
              <Key label="x³"  onClick={() => press("^3")}   accent="purple" testid="calc-cube" />
              <Key label="xʸ"  onClick={() => press("^")}    accent="purple" testid="calc-pow" />
              <Key label="!"   onClick={() => press("!")}    accent="purple" testid="calc-fact" />
              <Key label="π"   onClick={() => press("π")}    accent="purple" testid="calc-pi" />
              <Key label="e"   onClick={() => press("e")}    accent="purple" testid="calc-e" />
              <Key label="("   onClick={() => press("(")}    accent="purple" testid="calc-lparen" />
              <Key label=")"   onClick={() => press(")")}    accent="purple" testid="calc-rparen" />
              <Key label="%"   onClick={() => press("%")}    accent="purple" testid="calc-pct" />
            </div>

            {/* Number pad */}
            <div className="grid grid-cols-4 gap-2">
              <Key label={<Trash2 size={16} />} onClick={clearAll} accent="rose" testid="calc-clear" />
              <Key label={<Delete size={16} />} onClick={backspace} accent="rose" testid="calc-back" />
              <Key label="/" onClick={() => press("/")} accent="blue" testid="calc-div" />
              <Key label="×" onClick={() => press("*")} accent="blue" testid="calc-mul" />

              <Key label="7" onClick={() => press("7")} testid="calc-7" />
              <Key label="8" onClick={() => press("8")} testid="calc-8" />
              <Key label="9" onClick={() => press("9")} testid="calc-9" />
              <Key label="−" onClick={() => press("-")} accent="blue" testid="calc-sub" />

              <Key label="4" onClick={() => press("4")} testid="calc-4" />
              <Key label="5" onClick={() => press("5")} testid="calc-5" />
              <Key label="6" onClick={() => press("6")} testid="calc-6" />
              <Key label="+" onClick={() => press("+")} accent="blue" testid="calc-add" />

              <Key label="1" onClick={() => press("1")} testid="calc-1" />
              <Key label="2" onClick={() => press("2")} testid="calc-2" />
              <Key label="3" onClick={() => press("3")} testid="calc-3" />
              <Key label="±" onClick={() => setExpr((s) => s.startsWith("-") ? s.slice(1) : `-${s}`)} accent="blue" testid="calc-sign" />

              <Key label="0" onClick={() => press("0")} testid="calc-0" />
              <Key label="." onClick={() => press(".")} testid="calc-dot" />
              <Key label="a/b" onClick={() => press("/")} testid="calc-frac" />
              <Key label="=" onClick={equals} accent="primary" testid="calc-equals" />
            </div>

            {/* History (compact) */}
            {history.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/8">
                <div className="font-mono text-[10px] tracking-[0.3em] text-faint mb-1">HISTORY</div>
                <div className="max-h-24 overflow-y-auto text-right font-mono text-[11px] space-y-0.5">
                  {history.map((h, i) => (
                    <button
                      key={i}
                      onClick={() => setExpr(h.expr)}
                      className="block w-full text-right hover:text-white text-soft"
                    >
                      <span className="text-faint">{h.expr}</span>
                      <span className="text-white ml-2">= {h.result}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function Key({ label, onClick, accent, testid }) {
  const styles = {
    primary: "bg-gradient-to-br from-blue-500 to-purple-500 text-white font-bold",
    blue:    "bg-blue-500/10 border border-blue-500/30 text-blue-2 hover:bg-blue-500/20",
    purple:  "bg-purple-500/10 border border-purple-500/30 text-purple-2 hover:bg-purple-500/20",
    rose:    "bg-rose-500/10 border border-rose-500/30 text-loss hover:bg-rose-500/20",
    default: "bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.07]",
  }[accent || "default"];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`h-11 rounded-lg flex items-center justify-center font-heading text-base sm:text-lg tracking-wide select-none active:scale-95 transition-transform ${styles}`}
    >
      {label}
    </button>
  );
}

// ─── Expression evaluator ──────────────────────────────────────────────────
// Handcrafted tiny recursive-descent parser so we don't pull in mathjs (~700KB).
// Supports the operations exposed by the keypad above + lets the user type
// expressions directly into the display.

function formatNumber(n) {
  if (Object.is(n, -0)) n = 0;
  if (Math.abs(n) >= 1e12 || (Math.abs(n) < 1e-6 && n !== 0)) {
    return n.toExponential(6).replace(/0+e/, "e");
  }
  return parseFloat(n.toFixed(10)).toString();
}

function evaluate(input, { degrees }) {
  // Pre-normalize tokens
  const text = input
    .replace(/×/g, "*")
    .replace(/÷/g, "/")
    .replace(/−/g, "-")
    .replace(/π/g, "PI")
    .replace(/√/g, "sqrt")
    .replace(/\be\b/g, "E");

  const tokens = tokenize(text);
  if (!tokens.length) return undefined;
  const parser = new Parser(tokens, { degrees });
  const value = parser.parseExpression();
  if (!parser.atEnd()) throw new Error("Unexpected token");
  return value;
}

function tokenize(s) {
  const tokens = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i++; continue; }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < s.length && /[0-9.]/.test(s[j])) j++;
      tokens.push({ t: "num", v: parseFloat(s.slice(i, j)) });
      i = j; continue;
    }
    if (/[A-Za-z]/.test(c)) {
      let j = i;
      while (j < s.length && /[A-Za-z]/.test(s[j])) j++;
      tokens.push({ t: "id", v: s.slice(i, j) });
      i = j; continue;
    }
    if ("+-*/^()%!".includes(c)) {
      tokens.push({ t: c }); i++; continue;
    }
    // unknown — skip silently to avoid breaking the live preview
    i++;
  }
  return tokens;
}

class Parser {
  constructor(tokens, opts) { this.toks = tokens; this.i = 0; this.opts = opts; }
  peek() { return this.toks[this.i]; }
  consume() { return this.toks[this.i++]; }
  atEnd() { return this.i >= this.toks.length; }
  match(t) { if (!this.atEnd() && this.peek().t === t) { this.i++; return true; } return false; }

  parseExpression() { return this.parseAddSub(); }

  parseAddSub() {
    let left = this.parseMulDiv();
    while (!this.atEnd() && (this.peek().t === "+" || this.peek().t === "-")) {
      const op = this.consume().t;
      const right = this.parseMulDiv();
      left = op === "+" ? left + right : left - right;
    }
    return left;
  }

  parseMulDiv() {
    let left = this.parseUnary();
    while (!this.atEnd() && (this.peek().t === "*" || this.peek().t === "/")) {
      const op = this.consume().t;
      const right = this.parseUnary();
      left = op === "*" ? left * right : left / right;
    }
    return left;
  }

  parseUnary() {
    if (this.peek()?.t === "-") { this.consume(); return -this.parseUnary(); }
    if (this.peek()?.t === "+") { this.consume(); return this.parseUnary(); }
    return this.parsePow();
  }

  parsePow() {
    let base = this.parsePostfix();
    if (!this.atEnd() && this.peek().t === "^") {
      this.consume();
      const exp = this.parseUnary();
      base = Math.pow(base, exp);
    }
    return base;
  }

  parsePostfix() {
    let v = this.parseAtom();
    while (!this.atEnd() && (this.peek().t === "!" || this.peek().t === "%")) {
      const op = this.consume().t;
      if (op === "!") v = factorial(v);
      else v = v / 100;
    }
    return v;
  }

  parseAtom() {
    const tok = this.peek();
    if (!tok) throw new Error("Empty");
    if (tok.t === "num") { this.consume(); return tok.v; }
    if (tok.t === "(") {
      this.consume();
      const v = this.parseExpression();
      if (!this.match(")")) throw new Error("Missing )");
      return v;
    }
    if (tok.t === "id") {
      this.consume();
      const name = tok.v.toLowerCase();
      if (name === "pi") return Math.PI;
      if (name === "e")  return Math.E;
      // function call expected
      const fn = FUNCTIONS[name];
      if (!fn) throw new Error(`Unknown ${name}`);
      if (!this.match("(")) throw new Error(`${name}(?`);
      const arg = this.parseExpression();
      if (!this.match(")")) throw new Error("Missing )");
      return fn(arg, this.opts);
    }
    throw new Error("Unexpected token");
  }
}

const FUNCTIONS = {
  sin:  (x, o) => Math.sin(o.degrees ? (x * Math.PI) / 180 : x),
  cos:  (x, o) => Math.cos(o.degrees ? (x * Math.PI) / 180 : x),
  tan:  (x, o) => Math.tan(o.degrees ? (x * Math.PI) / 180 : x),
  asin: (x, o) => { const r = Math.asin(x); return o.degrees ? (r * 180) / Math.PI : r; },
  acos: (x, o) => { const r = Math.acos(x); return o.degrees ? (r * 180) / Math.PI : r; },
  atan: (x, o) => { const r = Math.atan(x); return o.degrees ? (r * 180) / Math.PI : r; },
  log:  (x) => Math.log10(x),
  ln:   (x) => Math.log(x),
  sqrt: (x) => Math.sqrt(x),
  abs:  (x) => Math.abs(x),
  exp:  (x) => Math.exp(x),
};

function factorial(n) {
  if (n < 0 || Math.floor(n) !== n) return NaN;
  let acc = 1;
  for (let i = 2; i <= n; i++) acc *= i;
  return acc;
}
